import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, ChakraProvider, Heading, Text, extendTheme, useToast } from '@chakra-ui/react';
import { Outlet, useParams } from 'react-router-dom';
import { withI18NextTranslate } from 'sp-ui';
import { LoadingIndicator } from 'ts/common/components';
import ClientPageBanner from 'ts/common/components/ClientPageBanner';
import ClientPageLayout from 'ts/common/components/ClientPageLayout';
import { getTheme } from 'ts/common/components/gallery';
import { type IFetchHookResponse, useTranslate } from 'ts/common/hooks';
import { useBootstrapData, withBootstrapData } from 'client_react/bootstrap';
import { FetchMethod, useClientApiFetch } from 'client_react/bootstrap';

interface QuestionnaireError {
    detail?: string;
    status: number;
}

const isQuestionnaireError = (response: unknown): response is QuestionnaireError => {
    return (
        response !== null &&
        typeof response === 'object' &&
        'status' in response &&
        typeof (response as QuestionnaireError).status === 'number' &&
        (response as QuestionnaireError).status >= 400
    );
};

export type QuestionnaireContext = {
    fetchQuestionnaire: (
        email?: string
    ) => Promise<
        IFetchHookResponse<
            SpApi.Client.IQuestionnaireMinimal | SpApi.Client.IQuestionnaire | QuestionnaireError
        >
    >;
    loading: boolean;
    saveQuestionResponse: (questionId: number, responseText: string | null) => Promise<void>;
    saving: boolean;
    savingFailed: boolean;
    error: QuestionnaireError | null;
    questionnaire: SpApi.Client.IQuestionnaireMinimal | SpApi.Client.IQuestionnaire;
};

const QuestionnaireAppContent: React.FC = () => {
    const t = useTranslate('clientQuestionnaire');
    const toast = useToast();

    const { questionnaireId } = useParams<{ questionnaireId: string }>();
    const [error, setError] = useState<QuestionnaireError | null>(null);
    const [savingFailed, setSavingFailed] = useState(false);
    const [questionnaire, setQuestionnaire] = useState<
        SpApi.Client.IQuestionnaireMinimal | SpApi.Client.IQuestionnaire
    >();

    const { loading, performFetch } = useClientApiFetch<
        SpApi.Client.IQuestionnaire | QuestionnaireError
    >(`questionnaire/${questionnaireId}`, {
        defer: true,
        method: FetchMethod.GET
    });

    const { loading: saving, performFetch: patchQuestionnaire } = useClientApiFetch<unknown>(
        `questionnaire/${questionnaireId}`,
        {
            defer: true,
            method: FetchMethod.PATCH
        }
    );

    const fetchQuestionnaire = useCallback(
        async (email?: Nullable<string>) => {
            const response = await performFetch({
                search: email ? { email } : undefined
            });

            if (response && !isQuestionnaireError(response)) {
                setQuestionnaire(response);
                setError(null);

                if (email) {
                    sessionStorage.setItem(`questionnaire-email-${questionnaireId}`, email);
                }
            } else {
                setError(response);
            }

            return response;
        },
        [performFetch, questionnaireId]
    );

    const saveQuestionResponse = useCallback(
        async (questionId: number, responseText: string | null) => {
            const email = sessionStorage.getItem(`questionnaire-email-${questionnaireId}`);

            const response = await patchQuestionnaire({
                data: { id: questionId, response_text: responseText },
                search: email ? { email } : undefined
            });

            if (response && !isQuestionnaireError(response)) {
                setError(null);
                setSavingFailed(false);
                setQuestionnaire((prev) => {
                    if (!prev || !('pages' in prev)) {
                        return prev;
                    }

                    return {
                        ...prev,
                        questionnaireStatus: 'partial',
                        pages: prev.pages.map((page) => ({
                            ...page,
                            questions: page.questions.map((q) =>
                                q.id === questionId ? { ...q, responseText } : q
                            )
                        }))
                    };
                });
            } else {
                setError(response);
                setSavingFailed(true);
                toast({
                    title: t('error'),
                    description: response?.detail ?? t('errorSavingQuestionnaire'),
                    status: 'error'
                });
            }
        },
        [patchQuestionnaire, questionnaireId, toast, t]
    );

    useEffect(() => {
        const email = sessionStorage.getItem(`questionnaire-email-${questionnaireId}`);

        (async () => {
            const response = await fetchQuestionnaire(email);

            if (response?.status === 403) {
                sessionStorage.removeItem(`questionnaire-email-${questionnaireId}`);
                await fetchQuestionnaire();
            }
        })();
    }, [fetchQuestionnaire, questionnaireId]);

    const theme = useMemo(
        () => (questionnaire ? extendTheme(getTheme(questionnaire.brandTheme)) : extendTheme()),
        [questionnaire]
    );

    return (
        <ChakraProvider resetCSS={false} theme={theme}>
            {questionnaire ? (
                <ClientPageLayout
                    banner={<ClientPageBanner>{questionnaire.name}</ClientPageBanner>}
                    headingText={questionnaire.brandName}
                    showLegalLinks
                >
                    <Outlet
                        context={
                            {
                                fetchQuestionnaire,
                                loading,
                                saveQuestionResponse,
                                saving,
                                savingFailed,
                                error,
                                questionnaire
                            } satisfies QuestionnaireContext
                        }
                    />
                </ClientPageLayout>
            ) : loading ? (
                <LoadingIndicator />
            ) : error?.status === 404 ? (
                <Box>
                    <Heading as="h1">{t('questionnaireNotFound')}</Heading>
                    <Text>{t('questionnaireNotFoundMessage')}</Text>
                </Box>
            ) : (
                <Box>
                    <Heading as="h1">{t('errorLoadingQuestionnaireTitle')}</Heading>
                    <Text>{t('errorLoadingQuestionnaire')}</Text>
                </Box>
            )}
        </ChakraProvider>
    );
};

const QuestionnaireApp: React.FC = withBootstrapData(() => {
    const { translations } = useBootstrapData();

    const PageWithTranslations = useMemo(
        () => withI18NextTranslate(QuestionnaireAppContent, translations),
        [translations]
    );

    return <PageWithTranslations />;
});

export default QuestionnaireApp;
